import { ComponentType, FunctionComponent, lazy, Suspense, useCallback, useEffect, useRef } from "react";
import { Placeholder } from "@ppb/the-wall-web/components/bricks/Placeholder/Placeholder";

import { APOLLO_MIGRATED_CARDS } from "@ppb/tbd-store/state/layout/cards/Card.types";

import ConnectedCard from "../../Card";
import TBDCard, { hasMigratedComponent } from "../../Card/Card.web";
import { ErrorBoundary } from "../../ErrorBoundary/ErrorBoundary";
import { useSelectedCardIsLoaded } from "../useSelectedCardIsLoaded";
import { useMigratedPebbleGate } from "../useMigratedPebbleGate";

import gridLayout from "../../GridCard/GridCardPlaceholder.web.css";
import correctScoreLayout from "../../CorrectScoreCard/CorrectScoreCardPlaceholder.web.css";
import styles from "./PebbleContent.web.css";

/* Inner cards are code-split in Card.web.tsx; keep them lazy here too so PebbleCardGroup
  doesn't pull every card chunk into its own bundle. */
const ConnectedGridCard = lazy(() => import(/* webpackChunkName: "GridCard" */ "../../GridCard"));
const GridCard = lazy(() => import(/* webpackChunkName: "GridCard" */ "../../GridCard/GridCard.web"));
// Both MarketCards are reachable: which one renders is decided per render by the MARKET_CARD_APOLLO
// throttle, so the migrated card can be switched off in production without a revert.
const ConnectedMarketCard = lazy(() => import(/* webpackChunkName: "MarketCard" */ "../../MarketCard"));
const LegacyMarketCard = lazy(() => import(/* webpackChunkName: "MarketCard" */ "../../MarketCard/MarketCard.web"));
// Wrapper (not the raw package card) so the exchange inline betslip is injected
// for pebble-rendered market cards too (PebbleContent renders the migrated card
// directly, bypassing the Card dispatcher). See MarketCardWrapper.
const MigratedMarketCard = lazy(
  () => import(/* webpackChunkName: "MarketCardApollo" */ "../../MarketCardWrapper/MarketCardWrapper.web"),
);
// Both GridCards are reachable too; GRID_CARD_APOLLO picks one per render.
const MigratedGridCard = lazy(
  () => import(/* webpackChunkName: "GridCardApollo" */ "../../GridCardWrapper/GridCardWrapper.web"),
);
const ConnectedCorrectScoreCard = lazy(
  () => import(/* webpackChunkName: "CorrectScoreCard" */ "../../CorrectScoreCard"),
);
const CorrectScoreCard = lazy(
  () => import(/* webpackChunkName: "CorrectScoreCard" */ "../../CorrectScoreCard/CorrectScoreCard.web"),
);
const ConnectedPackagedCreatedBetsCard = lazy(
  () => import(/* webpackChunkName: "PackagedCreatedBetsCard" */ "../../PackagedCreatedBetsCard"),
);
const PackagedCreatedBetsCard = lazy(
  () =>
    import(
      /* webpackChunkName: "PackagedCreatedBetsCard" */ "../../PackagedCreatedBetsCard/PackagedCreatedBetsCard.web"
    ),
);

/* Pebble variants of each card's placeholder: the card's skeleton layout repainted with the
pebble content surface. GridCard/CorrectScoreCard borrow their shared placeholder's layout;
MarketCard and PackagedCreatedBetsCard keep their pebble layout in PebbleContent's own
styles (the shared placeholders stay untouched — MarketCard's pebble floor height differs
from its main-layout placeholder). */
const GridCardPebblePlaceholder: FunctionComponent = () => (
  <div className={gridLayout.placeholder}>
    <Placeholder className={styles.surface} />
  </div>
);
const MarketCardPebblePlaceholder: FunctionComponent = () => (
  <div className={styles.marketCard}>
    <Placeholder className={styles.surface} />
  </div>
);
const CorrectScoreCardPebblePlaceholder: FunctionComponent = () => (
  <div className={correctScoreLayout.placeholder}>
    <Placeholder className={styles.surface} />
  </div>
);
const PackagedCreatedBetsCardPebblePlaceholder: FunctionComponent = () => (
  <div className={styles.packagedCreatedBetsCard}>
    <Placeholder className={styles.surface} />
  </div>
);

type PebbleCardEntry = {
  Connected: ComponentType<any>;
  Component: ComponentType<any>;
  Placeholder: FunctionComponent;
};

// Only the cards that need a pebble-surface placeholder are listed here; every other
// typename falls through to the generic Card aggregator (TBDCard), unchanged.
const pebbleCards: Record<string, PebbleCardEntry> = {
  GridCard: { Connected: ConnectedGridCard, Component: GridCard, Placeholder: GridCardPebblePlaceholder },
  MarketCard: {
    Connected: ConnectedMarketCard,
    Component: LegacyMarketCard,
    Placeholder: MarketCardPebblePlaceholder,
  },
  CorrectScoreCard: {
    Connected: ConnectedCorrectScoreCard,
    Component: CorrectScoreCard,
    Placeholder: CorrectScoreCardPebblePlaceholder,
  },
  PackagedCreatedBetsCard: {
    Connected: ConnectedPackagedCreatedBetsCard,
    Component: PackagedCreatedBetsCard,
    Placeholder: PackagedCreatedBetsCardPebblePlaceholder,
  },
};

type MigratedPebbleCardEntry = {
  Component: ComponentType<any>;
  Placeholder: FunctionComponent;
};

// Apollo-migrated cards render self-contained (no Connected wrapper) but still need
// their pebble-surface placeholder, passed to the card rather than to a connector.
const migratedPebbleCards: Record<string, MigratedPebbleCardEntry> = {
  MarketCard: { Component: MigratedMarketCard, Placeholder: MarketCardPebblePlaceholder },
  GridCard: { Component: MigratedGridCard, Placeholder: GridCardPebblePlaceholder },
};

// Which inner-card typenames emit onContentReady (a real "content painted" signal). Those release the
// reserved tab height on that signal; others fall back to their Redux loaded state.
const signalsContentReady = (typename: string): boolean => typename in migratedPebbleCards;

// Whether a typename reports readiness at all. The container asks this before render, so it doesn't
// reserve a height floor nothing will ever release. Three ways to report: the card's own settle signal, or
// — for the cards released from their Redux loaded state — being a listed Apollo card (loaded on first
// render) or being Redux-rendered (loaded once its card lands in the layout slices). Only an Apollo-rendered
// typename in neither registry can never report, and that one gets no floor.
//
// It lives here, next to the registry the first term reads, because the container's question and the
// per-path release above are answered from the same facts. The settle-signal term is also what survives
// graduation: once a migrated card joins migratedCardWhiteList the other two terms both go false for it,
// and its floor would silently disappear.
//
// Deliberately blind to the per-card throttles, which the container cannot read: with one off, that card
// renders from Redux, and the third term already answers true for it.
export const contentSignalsReady = (typename: string): boolean =>
  signalsContentReady(typename) || APOLLO_MIGRATED_CARDS.includes(typename) || !hasMigratedComponent(typename);

// Typenames whose blurb the experiment replaces with the group's icon. The other legacy cards are not handed
// the prop, so it does not reach connectors that have no use for it.
const SUPER_SUB_BLURB_CARDS = ["MarketCard", "GridCard"];

type PebbleContentProps = {
  urn: string;
  typename: string;
  visible?: boolean;
  // PebbleCardGroup's own outer container (title included), for title-less cards like GridCard.
  outerCardRef?: HTMLDivElement | null;
  // Reports the selected card as ready, so the group can release its height floor.
  onReady?: () => void;
  // From PebbleCardGroup: when the super sub experiment is on, the group header shows the icon and the cards
  // inside drop their blurb. Only MarketCard and GridCard read it; the other typenames never see it.
  isSuperSubExperimentActive?: boolean;
};

/**
 * Renders the card selected inside a PebbleCardGroup. For the cards we paint with the pebble
 * surface we render their inner connected component directly with a pebble placeholder (the
 * inner connector shows it while data is loading); every other card forwards to the generic
 * Card aggregator so its behaviour is untouched.
 */
const PebbleContent: FunctionComponent<PebbleContentProps> = ({
  urn,
  typename,
  visible,
  outerCardRef,
  onReady,
  isSuperSubExperimentActive = false,
}) => {
  const isLoaded = useSelectedCardIsLoaded(urn, typename);
  // Switched off, the typename falls back to its legacy Redux entry in `pebbleCards` (see the hook).
  // Readiness below keys on the entry that rendered, so a switched-off card still releases the floor from
  // its Redux loaded state.
  const isMigrated = useMigratedPebbleGate(urn, typename);
  const migratedEntry = isMigrated ? migratedPebbleCards[typename] : undefined;

  // A migrated card paints from its own Apollo fetch, so the Redux loaded state is the wrong trigger for
  // it: that tracks this host's FETCH_CARDS landing, which says nothing about whether the card has painted.
  // Release on the card's own settle signal instead (wired into the migrated branch below); everything else
  // falls back to its loaded state — including the legacy card this falls back to, which emits no signal.
  const settlesOnContentReady = !!migratedEntry;

  // Fire on a change of selection rather than on every change of `onReady`, whose identity depends on how
  // the caller memoizes it. Only the last urn is remembered, so switching away and back signals twice;
  // repeat calls happen to be harmless, but that is the caller's internals, not a contract. Both paths go
  // through this one guard, so the card re-signalling on a later load cycle cannot double-fire either.
  const readyUrn = useRef<string | null>(null);
  const signalReady = useCallback(() => {
    if (readyUrn.current === urn) {
      return;
    }
    readyUrn.current = urn;
    onReady?.();
  }, [onReady, urn]);

  useEffect(() => {
    if (settlesOnContentReady || !isLoaded) {
      return;
    }
    signalReady();
  }, [settlesOnContentReady, isLoaded, signalReady]);

  // Forget the last signal when the selection changes, so returning to a pebble can signal again. Without
  // this, A → a pebble that never loads → A leaves the ref on A, the effect skips, and the floor the
  // container reserved on that press is never released — release is only reachable through onReady. The
  // Redux component recomputed its loaded flag every render, so a return always dropped the floor.
  useEffect(
    () => () => {
      readyUrn.current = null;
    },
    [urn],
  );

  // Hooks stay above this early return: the fallthrough is per-typename, so a conditional hook here would
  // change hook order between typenames.
  if (migratedEntry) {
    const { Component, Placeholder: PebblePlaceholder } = migratedEntry;

    return (
      <ErrorBoundary urn={urn}>
        {/* key={urn}: remount per pebble so transient card state (e.g. show-more) doesn't bleed */}
        {/* fallback covers the lazy chunk load; `placeholder` covers the card's own data
            load, which is what the Redux connector's `placeholder` prop used to do. */}
        <Suspense key={urn} fallback={<PebblePlaceholder />}>
          {/* outerCardRef: the pebble-level node, forwarded like the legacy branch below does. The
              card scrolls to it on show-more collapse, which is what keeps the group header in view. */}
          <Component
            urn={urn}
            visible={visible}
            onContentReady={signalReady}
            placeholder={PebblePlaceholder}
            outerCardRef={outerCardRef}
            isSuperSubExperimentActive={isSuperSubExperimentActive}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  const entry = pebbleCards[typename];

  if (!entry) {
    return <ConnectedCard urn={urn} component={TBDCard} typename={typename} visible={visible} />;
  }

  const { Connected, Component, Placeholder: PebblePlaceholder } = entry;

  return (
    <ErrorBoundary urn={urn}>
      {/* key={urn}: remount per pebble so transient card state (e.g. show-more) doesn't bleed */}
      <Suspense key={urn} fallback={<PebblePlaceholder />}>
        <Connected
          urn={urn}
          component={Component}
          placeholder={PebblePlaceholder}
          visible={visible}
          outerCardRef={outerCardRef}
          {...(SUPER_SUB_BLURB_CARDS.includes(typename) && { isSuperSubExperimentActive })}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export default PebbleContent;
